import { cn } from 'utils'

type ComparisonStatus = 'unchanged' | 'removed_or_changed' | 'added_or_changed'

interface ComparisonValue {
  value: string;
  status?: ComparisonStatus;
}

interface RenewalsConfirmComparisonTableProps {
  prevPlanYear: number;
  currPlanYear: number;
  prevValues: Array<string | ComparisonValue>;
  currValues: Array<string | ComparisonValue>;
}

const HEADER_CELL_STYLES = 'body-bold border-b border-gray-200 p-2 xs:p-4'
const CELL_STYLES = 'px-2 xs:px-4 py-1.5 body text-gray-800'
const LEFT_CELL_STYLES = 'border-r border-gray-200'
const RIGHT_CELL_STYLES = 'bg-gray-50 body text-gray-800'

const RenewalsConfirmComparisonTable: React.FC<
  RenewalsConfirmComparisonTableProps
> = ({ prevPlanYear, currPlanYear, prevValues, currValues }) => {
  const maxLength = Math.max(prevValues.length, currValues.length)

  // Helper to normalize values to ComparisonValue objects
  const normalizeValue = (
    val: string | ComparisonValue | undefined
  ): ComparisonValue => {
    if (!val) return { value: '', status: 'unchanged' }
    if (typeof val === 'string') return { value: val, status: 'unchanged' }
    return { value: val.value, status: val.status ?? 'unchanged' }
  }

  return (
    <div className='w-full border border-gray-200 rounded-lg grid grid-cols-2 overflow-hidden'>
      {/* Year headers */}
      <div
        className={cn(HEADER_CELL_STYLES, LEFT_CELL_STYLES, 'text-gray-500')}
      >
        {prevPlanYear}
      </div>
      <div
        className={cn(HEADER_CELL_STYLES, RIGHT_CELL_STYLES, 'text-thatch-500')}
      >
        {currPlanYear}
      </div>
      {/* left column: current year values; right column: previous year values */}
      {Array.from({ length: maxLength }, (_, i) => {
        const prevVal = normalizeValue(prevValues[i])
        const currVal = normalizeValue(currValues[i])

        return [
          <div
            key={`prev-${i}`}
            className={cn(CELL_STYLES, LEFT_CELL_STYLES, {
              'pt-4': i === 0,
              'pb-4': i === maxLength - 1,
              'line-through text-gray-400':
                prevVal.status === 'removed_or_changed',
            })}
          >
            {prevVal.value}
          </div>,
          <div
            key={`curr-${i}`}
            className={cn(CELL_STYLES, RIGHT_CELL_STYLES, {
              'pt-4': i === 0,
              'pb-4': i === maxLength - 1,
              'body-bold': currVal.status === 'added_or_changed',
            })}
          >
            {currVal.value}
          </div>,
        ]
      }).flat()}
    </div>
  )
}

export default RenewalsConfirmComparisonTable
